import type { FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useForm, useFormState, useController } from "react-hook-form";
import { Info } from "lucide-react";
import { useCreateObservationCategory } from "src/features/Admin/ObservationsCategories/hooks/useCreateObservationCategory";
import { useUpdateObservationCategory } from "src/features/Admin/ObservationsCategories/hooks/useUpdateObservationCategory";
import { z } from "zod";
import { Button } from "src/components/ui/button";
import GroupsAudienceCard from "src/components/GroupsAudienceCard/GroupsAudienceCard";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";

const CreateNewObservationsCategorySchema = z.object({
	name: z.string().min(1, "Name is required"),
	audienceGroupIds: z.array(z.number()).default([])
});

type FormSchema = z.infer<typeof CreateNewObservationsCategorySchema>;

export type CreateNewObservationsCategoryFormProps = {
	defaultValues?: FormSchema;

	categoryId?: number;

	isLoading?: boolean;

	onClose: () => void;
};

const CreateNewObservationsCategoryForm = ({
	defaultValues,
	categoryId,
	isLoading,
	onClose
}: CreateNewObservationsCategoryFormProps) => {
	const [serverError, setServerError] = useState<string | null>(null);

	const resolver = zodResolver(CreateNewObservationsCategorySchema);

	const { register, handleSubmit, control, reset } = useForm<FormSchema>({
		resolver,
		defaultValues
	});

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues]);

	const { isValid, errors } = useFormState({ control });

	// Don't pass `defaultValue` here — react-hook-form would treat the literal
	// `[]` as the registered default and could override a non-empty value
	// supplied via `defaultValues.audienceGroupIds` on the edit modal.
	const { field: audienceGroupIds } = useController({
		control,
		name: "audienceGroupIds"
	});

	const {
		data: adminGroups = [],
		isLoading: groupsLoading,
		isError: groupsError
	} = useAdminGroups();

	// Memoise the picker's options so a keystroke in the category name doesn't
	// rebuild the array and re-render GroupsAudienceCard.
	const groupOptions = useMemo(
		() => adminGroups.map((g) => ({ id: Number(g.id), name: g.name })),
		[adminGroups]
	);

	const {
		mutateAsync: createObservationCategory,
		isLoading: isCreatingObservationCategory
	} = useCreateObservationCategory();

	const {
		mutateAsync: updateObservationCategory,
		isLoading: isUpdatingObservationCategory
	} = useUpdateObservationCategory();

	const onValid = async (data: FormSchema) => {
		const { name, audienceGroupIds: groupIds } = data;

		try {
			if (categoryId) {
				await updateObservationCategory({
					name,
					categoryId,
					audienceGroupIds: groupIds
				});
			} else {
				await createObservationCategory({
					name,
					audienceGroupIds: groupIds
				});
			}

			onClose();
		} catch (error) {
			console.error("Error creating observation category", error);

			if (error instanceof AxiosError) {
				const message = error.response?.data.name[0];
				setServerError(message);
			}
		}
	};

	const onInvalid = (errors: FieldErrors<FormSchema>) => {
		console.warn("Validation error", errors);
	};

	const hasError = Boolean(serverError) || Boolean(errors.name);

	return (
		<div>
			<form onSubmit={handleSubmit(onValid, onInvalid)}>
				<div className={hasError ? "text-red-600" : ""}>
					<div className="flex flex-row items-end justify-between mb-1">
						<label className="text-base">Category title</label>
						<span className="text-xs font-normal">Required</span>
					</div>

					<div className="relative">
						<input
							type="text"
							className={`w-full h-10 px-3 bg-white rounded border ${hasError ? "border-red-600" : "border-border"} disabled:opacity-50`}
							placeholder="Please enter category title"
							disabled={isLoading}
							{...register("name")}
						/>

						{isLoading && (
							<span className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
						)}
					</div>

					{serverError && (
						<p className="text-destructive mt-1 text-xs flex items-center gap-1">
							<Info size={16} />
							{serverError}
						</p>
					)}
				</div>

				<div className="mt-6">
					<GroupsAudienceCard
						groupIds={audienceGroupIds.value ?? []}
						onChange={audienceGroupIds.onChange}
						groups={groupOptions}
						isLoading={groupsLoading}
						isError={groupsError}
						helperText="Forms inside this category inherit this audience."
					/>
					{groupsError && (
						<p
							role="alert"
							className="text-destructive mt-2 text-xs flex items-center gap-1"
						>
							<Info size={16} />
							Couldn’t load groups. Saving now would overwrite the audience with
							no groups. Reload and try again.
						</p>
					)}
				</div>

				<div className="flex justify-between mt-10">
					<Button
						variant="outline"
						type="button"
						onClick={onClose}
						className="min-w-[160px]"
					>
						Cancel
					</Button>

					<Button
						variant="default"
						type="submit"
						className="min-w-[160px]"
						disabled={
							!isValid ||
							isCreatingObservationCategory ||
							isUpdatingObservationCategory ||
							groupsError
						}
					>
						{isCreatingObservationCategory || isUpdatingObservationCategory
							? "Loading..."
							: categoryId
								? "Update"
								: "Create"}
					</Button>
				</div>
			</form>
		</div>
	);
};

export default CreateNewObservationsCategoryForm;
